import { useEffect, useMemo } from 'react'

import {
  Box,
  Button,
  DragNDrop,
  Input,
  Modal,
  Text,
} from '@fairfashion/design-system'
import { Controller, useForm } from 'react-hook-form'
import { useTheme } from 'styled-components'

import { sdk } from '../../services/sdk'

import * as Styles from './invoice-form.styles'
import { InvoiceFormProps } from './invoice-form.types'

export interface InvoiceForm {
  invoiceCode?: string
  invoiceKey?: string
  invoiceCreateDate?: string
  file?: File | null
}

export const InvoiceForm = ({
  onSubmit,
  open,
  setOpen,
  data,
}: InvoiceFormProps) => {
  const { register, handleSubmit, getValues, control, reset } =
    useForm<InvoiceForm>({
      defaultValues: {} as InvoiceForm,
    })

  const theme = useTheme()

  const loadFormInEditMode = () => {
    const currentData: Partial<InvoiceForm> = {
      file: null,
      invoiceCode: data?.invoiceCode ?? '',
      invoiceCreateDate: data?.invoiceCreateDate ?? '',
      invoiceKey: data?.invoiceKey ?? '',
    }

    reset(currentData)
  }

  useEffect(() => {
    if (open) {
      loadFormInEditMode()
    }
  }, [open])

  const onSubmitInvoice = async () => {
    const file = getValues().file

    let fileUrl: string | undefined = undefined
    if (file) {
      fileUrl = await sdk.invoices.uploadFile(file)
    }

    onSubmit({
      invoiceCode: getValues().invoiceCode,
      invoiceKey: getValues().invoiceKey,
      invoiceCreateDate: getValues().invoiceCreateDate,
      invoiceFile: fileUrl,
    })

    setOpen(false)
    reset({})
  }

  const isEmpty = useMemo(() => {
    return (
      !data?.invoiceCode &&
      !data?.invoiceKey &&
      !data?.invoiceCreateDate &&
      !data?.invoiceFile
    )
  }, [data])

  const invoiceFile = data?.invoiceFile
    ? data.invoiceFile.split('/').at(-1)?.split('?').at(0)
    : ''

  return (
    <>
      {!isEmpty && (
        <Box
          width="100%"
          border={{ width: 1, color: 'grey_4' }}
          borderRadius={theme.border_radius.card}
          padding={2}
        >
          <Box width="100%" flex={1} gap={8} justifyContent="space-between">
            <Styles.Container maxWidth="1000px" justifyContent="space-between">
              <Box direction="column">
                <Text size="small" color="grey_3">
                  Código
                </Text>
                <Text size="small" fontWeight={500}>
                  {data?.invoiceCode ?? ''}
                </Text>
              </Box>
              <Box direction="column">
                <Text size="small" color="grey_3">
                  Chave
                </Text>
                <Text size="small" fontWeight={500}>
                  {data?.invoiceKey ?? ''}
                </Text>
              </Box>
              <Box direction="column">
                <Text size="small" color="grey_3">
                  Data de emissão
                </Text>
                <Text size="small" fontWeight={500}>
                  {data?.invoiceCreateDate ?? ''}
                </Text>
              </Box>
              <Box direction="column">
                <Text size="small" color="grey_3">
                  Arquivo NF
                </Text>
                <a href={data?.invoiceFile} target="_blank">
                  <Text size="small" color="info" fontWeight={500}>
                    {invoiceFile}
                  </Text>
                </a>
              </Box>
            </Styles.Container>
          </Box>
        </Box>
      )}
      <Modal
        open={open}
        onClose={setOpen}
        maxWidth="830px"
        width="100%"
        customTitle={
          <Text textTransform="uppercase" size="small" color="grey_3">
            Adicionar Nota Fiscal
          </Text>
        }
      >
        <Box width="100%" direction="column" padding={1}>
          <form onSubmit={handleSubmit(onSubmitInvoice)}>
            <Box direction="column" width="100%" gap={2}>
              <Box gap={2} width="100%" flexWrap="wrap">
                <Box flex={1} minWidth="180px">
                  <Input {...register('invoiceCode')} label="Código" />
                </Box>
                <Box flex={1} minWidth="180px">
                  <Input {...register('invoiceKey')} label="Chave" />
                </Box>
                <Box flex={1} minWidth="180px">
                  <Input
                    {...register('invoiceCreateDate')}
                    label="Data de emissão"
                    mask="00/00/0000"
                  />
                </Box>
              </Box>
              <Box width="100%">
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <DragNDrop
                      onChange={(files) => field.onChange(files?.[0])}
                      fileTypeError="Tipo de arquivo inválido"
                    />
                  )}
                />
              </Box>
              <Box
                width="100%"
                alignItems="flex-end"
                direction="column"
                gap={2}
              >
                <Box gap={2}>
                  <Button variant="outlined" onClick={() => setOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit">Salvar</Button>
                </Box>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  )
}
